/**
 * GTM Event Queue Manager
 *
 * Ensures that page_view events are always pushed to the dataLayer first,
 * before any other analytics events (view_item, view_item_list, etc.).
 *
 * This prevents race conditions caused by asynchronous user authentication
 * and metadata resolution.
 */

import { sendGTMEvent } from '@next/third-parties/google';

import { GA_DEBUG_MODE } from '@/lib/const';

import { EVENT_NAME } from './const';

type QueuedEvent = {
  event: any;
  id: string;
  requiresInit: boolean;
  timestamp: number;
};

class GTMEventQueue {
  private isPageViewInitialized = false;
  private queue: QueuedEvent[] = [];
  private eventIdCounter = 0;
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly QUEUE_TIMEOUT_MS = 10000; // 10 seconds safety timeout

  /**
   * Push an event to the queue or directly to dataLayer
   * @param event - The GTM event data
   * @param requiresInit - Whether this event requires page_view to fire first
   */
  public pushEvent(event: any, requiresInit = false): void {
    if (typeof window === 'undefined') {
      // SSR - skip queuing
      return;
    }

    const eventId = `evt_${++this.eventIdCounter}`;
    const isPageView = event.event === EVENT_NAME.PAGE_VIEW;

    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Event:', {
        eventId,
        eventType: event.event,
        requiresInit,
        isInitialized: this.isPageViewInitialized,
        queueSize: this.queue.length,
      });
    }

    // Page view events always go through immediately and mark initialization
    if (isPageView) {
      this.sendToDataLayer(event, eventId);
      this.markPageViewComplete();
      return;
    }

    // If event doesn't require initialization, send immediately
    if (!requiresInit) {
      this.sendToDataLayer(event, eventId);
      return;
    }

    // If already initialized, send immediately
    if (this.isPageViewInitialized) {
      this.sendToDataLayer(event, eventId);
      return;
    }

    // Otherwise, queue the event
    this.queueEvent(event, eventId, requiresInit);
  }

  public markPageViewComplete(): void {
    if (this.isPageViewInitialized) {
      return; // Already initialized
    }

    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Page view initialized, flushing queue');
    }

    this.isPageViewInitialized = true;
    this.flushQueue();
  }

  public reset(): void {
    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Resetting queue');
    }

    this.isPageViewInitialized = false;
    this.queue = [];
  }

  public getState(): {
    isInitialized: boolean;
    queueSize: number;
    queuedEvents: string[];
  } {
    return {
      isInitialized: this.isPageViewInitialized,
      queueSize: this.queue.length,
      queuedEvents: this.queue.map((e) => e.event.event || 'unknown'),
    };
  }

  private queueEvent(event: any, eventId: string, requiresInit: boolean): void {
    // Safety check: prevent queue overflow
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      console.warn(
        '[GTM Queue] Queue size exceeded, auto-flushing. This may indicate an issue.',
      );
      this.forceFlush();
    }

    const queuedEvent: QueuedEvent = {
      id: eventId,
      event,
      timestamp: Date.now(),
      requiresInit,
    };

    this.queue.push(queuedEvent);

    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Event queued:', {
        eventId,
        eventType: event.event,
        queueSize: this.queue.length,
      });
    }

    // Safety timeout: auto-flush if page_view doesn't fire
    setTimeout(() => {
      if (!this.isPageViewInitialized && this.queue.length > 0) {
        console.warn(
          '[GTM Queue] Timeout reached without page_view, force flushing',
        );
        this.forceFlush();
      }
    }, this.QUEUE_TIMEOUT_MS);
  }

  /**
   * Flush all queued events to dataLayer
   */
  private flushQueue(): void {
    if (this.queue.length === 0) {
      return;
    }

    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Flushing queue:', {
        eventCount: this.queue.length,
        events: this.queue.map((e) => e.event.event),
      });
    }

    // Process queue in FIFO order
    const eventsToFlush = [...this.queue];
    this.queue = [];

    eventsToFlush.forEach((queuedEvent) => {
      this.sendToDataLayer(queuedEvent.event, queuedEvent.id);
    });
  }

  /**
   * Force flush without waiting for page_view (emergency safety valve)
   */
  private forceFlush(): void {
    this.isPageViewInitialized = true; // Mark as initialized to prevent re-queuing
    this.flushQueue();
  }

  /**
   * Send event to GTM dataLayer
   */
  private sendToDataLayer(event: any, eventId: string): void {
    if (GA_DEBUG_MODE) {
      console.log('[GTM Queue] Sending to dataLayer:', {
        eventId,
        eventType: event.event,
        timestamp: Date.now(),
      });
    }

    // Ensure dataLayer exists
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }

    // Send using Next.js third-party library
    sendGTMEvent(event);
  }
}

// Singleton instance
let queueInstance: GTMEventQueue | null = null;

/**
 * Get the singleton GTM event queue instance
 */
export function getGTMEventQueue(): GTMEventQueue {
  if (!queueInstance) {
    queueInstance = new GTMEventQueue();
  }
  return queueInstance;
}

/**
 * Reset the queue (primarily for testing)
 */
export function resetGTMEventQueue(): void {
  if (queueInstance) {
    queueInstance.reset();
  }
}

/**
 * Get queue state for debugging
 */
export function getGTMQueueState() {
  return (
    queueInstance?.getState() || {
      isInitialized: false,
      queueSize: 0,
      queuedEvents: [],
    }
  );
}
